import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Footer, Topbar } from '../components/Chrome.tsx';
import { saveSession } from '../lib/session.ts';
import {
  COVER_PRESET,
  EventFields,
  ImagePicker,
  LOGO_PRESET,
  emptyEventForm,
  type EventFormValues,
} from '../components/EventForm.tsx';

export function NewEvent() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [values, setValues] = useState<EventFormValues>({ ...emptyEventForm, discipline: 'Straßenlauf · 10 km' });
  const [cover, setCover] = useState<string | null | undefined>(undefined);
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [pinValue, setPinValue] = useState('');
  const [deputyEmail, setDeputyEmail] = useState('');
  const [organizerName, setOrganizerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const change = (patch: Partial<EventFormValues>) => setValues((v) => ({ ...v, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{4,8}$/.test(pinValue)) return setError('PIN: 4–8 Ziffern.');
    setBusy(true);
    try {
      const { event } = await api.createEvent({
        ...values,
        startsAt: new Date(values.startsAt).toISOString(),
        organizerPin: pinValue,
        deputyEmail: deputyEmail.trim() || null,
        cover: cover ?? null,
        logo: logo ?? null,
      });
      saveSession(event.slug, {
        role: 'organizer',
        displayName: organizerName.trim() || values.club,
        organizerPin: pinValue,
      });
      navigate(`/events/${event.slug}/regie`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="app">
        <Topbar />
        <main className="shell section">
          <div className="empty">Moment …</div>
        </main>
      </div>
    );
  }

  // Ein Event gehoert immer einem Konto -- ohne Anmeldung geht es nicht.
  if (!user) return <Navigate to="/anmelden?weiter=%2Fevents%2Fnew" replace />;

  return (
    <div className="app">
      <Topbar />
      <main className="section">
        <div className="shell shell--form">
          <p className="eyebrow">Für Vereine</p>
          <h1 className="page-title">Event anlegen</h1>
          <p className="page-lede">
            Das Event erscheint sofort auf der Startseite. Alles hier lässt sich später in den Event-Einstellungen
            ändern — dort setzt du auch den Sendebereich auf der Karte.
          </p>

          <form className="card" onSubmit={submit}>
            <div className="card__body stack gap-16">
              <EventFields values={values} onChange={change} />

              <hr className="rule" />

              <ImagePicker
                label="Titelbild"
                hint="Hintergrund auf der Event-Seite und der Kachel. Quer, mindestens 1200 px breit."
                preset={COVER_PRESET}
                value={cover}
                onPick={setCover}
              />
              <ImagePicker
                label="Vereinslogo"
                hint="Rundes Profilbild neben dem Titel. Quadratisch, PNG mit Transparenz geht auch."
                preset={LOGO_PRESET}
                value={logo}
                shape="square"
                onPick={setLogo}
              />

              <hr className="rule" />

              <div className="field">
                <label>Haupt-Adresse (Admin)</label>
                <input className="input" value={user.email} readOnly disabled />
                <span className="hint">
                  Dein Konto. Der Admin verwaltet als Einziger die Regie-Liste und kann das Event bearbeiten.
                </span>
              </div>

              <div className="field">
                <label htmlFor="deputy">Vize-Adresse (optional)</label>
                <input
                  id="deputy"
                  className="input"
                  type="email"
                  value={deputyEmail}
                  onChange={(e) => setDeputyEmail(e.target.value)}
                  placeholder="zweite.person@verein.de"
                  autoComplete="off"
                />
                <span className="hint">
                  Zweite verantwortliche Person. Darf immer Regie führen, aber die Regie-Liste nicht ändern. Weitere
                  Personen trägst du später in den Einstellungen ein.
                </span>
              </div>

              <hr className="rule" />

              <div className="form-row">
                <div className="field">
                  <label htmlFor="pin">Veranstalter-PIN (Notfall-Zugang)</label>
                  <input
                    id="pin"
                    className="input"
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value)}
                    placeholder="4 Ziffern"
                    inputMode="numeric"
                  />
                  <span className="hint">
                    Für den Fall, dass jemand ohne Konto kurzfristig einspringen muss. Der reguläre Weg ist das Konto.
                  </span>
                </div>
                <div className="field">
                  <label htmlFor="oname">Dein Name in der Regie</label>
                  <input
                    id="oname"
                    className="input"
                    value={organizerName}
                    onChange={(e) => setOrganizerName(e.target.value)}
                    placeholder="Regie Elbufer"
                  />
                </div>
              </div>

              {error && <div className="notice">{error}</div>}

              <button className="btn btn--lg btn--block" type="submit" disabled={busy}>
                {busy ? 'Wird angelegt …' : 'Event anlegen und Regie öffnen'}
              </button>
              <p className="hint" style={{ textAlign: 'center' }}>
                Danach kannst du in den <Link className="linklike" to="/">Einstellungen</Link> den Sendebereich auf der
                Karte festlegen und weitere Regie-Adressen freischalten.
              </p>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
